
import axios from 'axios';


 const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

export interface GalleryItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  thumbnailUrl: string;
  linkUrl?: string;
  altText?: string;
  type: 'IMAGE' | 'VIDEO' | 'BANNER' | 'SLIDE' | 'LOGO' | 'ICON';
  orderIndex: number;
  active: boolean;
  metadata?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Gallery {
  id: string;
  name: string;
  description: string;
  active: boolean;
  items: GalleryItem[];
  createdAt: string;
  updatedAt: string;
}

class GalleryService {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearToken() {
    this.token = null;
    delete axios.defaults.headers.common['Authorization'];
  }

  private getHeaders() {
    return {
      headers: { 
        Authorization: `Bearer ${this.token}`
      }
    };
  }

  // Get all galleries (Admin only)
  async getAllGalleries(): Promise<Gallery[]> {
    const response = await axios.get(`${API_URL}/gallery`, this.getHeaders());
    return response.data;
  }

  // Get gallery by name (Admin only - with items)
  async getGalleryByName(name: string): Promise<Gallery> {
    const response = await axios.get(`${API_URL}/gallery/${name}`, this.getHeaders());
    return response.data;
  }

  // Upload image to gallery
async uploadGalleryItem(
  galleryName: string, 
  formData: FormData
): Promise<Gallery> {
  // Log the FormData contents for debugging
  console.log('Uploading to gallery:', galleryName);
  for (let pair of formData.entries()) {
    if (pair[0] === 'data') {
      console.log(pair[0], pair[1]);
      console.log('Data type:', typeof pair[1]);
    } else {
      console.log(pair[0], pair[1]);
    }
  }

  const response = await axios.post(
    `${API_URL}/gallery/${galleryName}/items`,
    formData,
    {
      headers: { 
        Authorization: `Bearer ${this.token}`,
        'Content-Type': 'multipart/form-data',
      },
      // Don't let axios transform the data
      transformRequest: [(data) => data],
    }
  );
  return response.data;
}

  // Update gallery item
  async updateGalleryItem(
    itemId: string,
    formData: FormData
  ): Promise<Gallery> {
    const response = await axios.put(
      `${API_URL}/gallery/items/${itemId}`,
      formData,
      {
        headers: { 
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'multipart/form-data',
        }
      }
    );
    return response.data;
  }

  // Delete gallery item
  async deleteGalleryItem(itemId: string): Promise<void> {
    await axios.delete(`${API_URL}/gallery/items/${itemId}`, this.getHeaders());
  }

  // Reorder items
  async reorderItems(galleryName: string, itemIds: string[]): Promise<void> {
    await axios.post(
      `${API_URL}/gallery/${galleryName}/reorder`,
      itemIds,
      this.getHeaders()
    );
  }

  // Get public gallery (no auth required)
  async getPublicGallery(name: string): Promise<Gallery> {
    const response = await axios.get(`${API_URL}/public/gallery/${name}`);
    return response.data;
  }

  // Get all active galleries (public)
  async getAllActiveGalleries(): Promise<Gallery[]> {
    const response = await axios.get(`${API_URL}/public/gallery/active`);
    return response.data;
  }
}

export const galleryService = new GalleryService();